import { frankencoinChainData, frankencoinConfigFor } from '@1delta/data-sdk'
import { FrankencoinPositionAbi } from '@1delta/abis'
import { erc20Abi } from 'viem'
import { multicallRetryUniversal } from '@1delta/providers'
import type { FrankencoinMarketRaw, FrankencoinMarketsRaw } from './types'

// 10 reads per market, in this order (keep in sync with the slicer below).
const READS_PER_MARKET = 10

// PER-CALL ABIs: the position reads and the collateral `balanceOf` target
// different contracts, and `balanceOf` exists only on the ERC-20. A merged
// ABI would be fine here (no name collisions), but the per-call array is
// cheap insurance against the decode-by-name trap that bit the USDD fetcher.
const FRANKENCOIN_ABI_PER_READ = [
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  FrankencoinPositionAbi,
  erc20Abi,
]

/**
 * Fetch all market data of ONE Frankencoin deployment — FULLY ON-CHAIN via
 * one retrying multicall over the curated ORIGINAL-position roster from
 * lender-metadata (`frankencoinConfig` / `frankencoinMarkets`, generated by
 * its `update:frankencoin` job, which filters to V2 + open + a priceable
 * collateral allowlist).
 *
 * Only originals are read here: they carry the terms AND the market-level
 * borrow capacity (`availableForClones`). Clones are USER positions and are
 * resolved per-account in the user-data path.
 *
 * NB `price` is the owner-DECLARED liquidation price, not an oracle quote —
 * see the converter for how that is surfaced.
 */
export async function fetchFrankencoinMarkets(
  lender: string,
  chainId: string,
): Promise<FrankencoinMarketsRaw> {
  const config = frankencoinConfigFor(lender, chainId)
  const chainData = frankencoinChainData(lender, chainId)
  const markets = chainData?.markets ?? []
  if (!config || markets.length === 0) {
    return { lender, config, chainData, markets: [] }
  }

  const calls = markets.flatMap((m) => [
    { address: m.position, name: 'price', params: [] },
    { address: m.position, name: 'minted', params: [] },
    { address: m.position, name: 'availableForClones', params: [] },
    { address: m.position, name: 'annualInterestPPM', params: [] },
    { address: m.position, name: 'calculateCurrentFee', params: [] },
    { address: m.position, name: 'reserveContribution', params: [] },
    { address: m.position, name: 'challengedAmount', params: [] },
    { address: m.position, name: 'expiration', params: [] },
    // Read LIVE rather than trusting the generator's snapshot: a position
    // can close between metadata runs (and dropping below
    // `minimumCollateral` closes one permanently), and publishing a dead
    // market as borrowable is the worst failure mode here.
    { address: m.position, name: 'isClosed', params: [] },
    { address: m.collToken, name: 'balanceOf', params: [m.position] },
  ])

  let results: any[] = []
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: markets.flatMap(() => FRANKENCOIN_ABI_PER_READ) as any,
      allowFailure: true,
    })) as any[]
  } catch {
    return { lender, config, chainData, markets: [] }
  }

  const big = (v: any): bigint | null => {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number') return BigInt(v)
    return null
  }

  const out: FrankencoinMarketRaw[] = markets.map((market, i) => {
    const base = i * READS_PER_MARKET
    const expiration = big(results[base + 7])
    return {
      market,
      price: big(results[base]),
      minted: big(results[base + 1]),
      availableForClones: big(results[base + 2]),
      annualInterestPPM: big(results[base + 3]),
      currentFeePPM: big(results[base + 4]),
      reserveContribution: big(results[base + 5]),
      challengedAmount: big(results[base + 6]),
      expiration,
      isClosed:
        typeof results[base + 8] === 'boolean'
          ? (results[base + 8] as boolean)
          : null,
      collateralBalance: big(results[base + 9]),
    }
  })

  return { lender, config, chainData, markets: out }
}
